<script setup>
import { ref, computed, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { PhCalendarBlank } from '@phosphor-icons/vue'

import Modal   from '@pages/Shared/Modal.vue'
import Input   from '@pages/Shared/form/Input.vue'
import Spinner from '@pages/Shared/ui/Spinner.vue'

import { flashToast } from '@/toast.js'

/* -------------------- EXPOSE open() -------------------- */
const show       = ref(false)
const isEditing  = ref(false)
const editingId  = ref(null)
const sessions   = ref([])   // kept so isSessionClosed can find the live session

const open = (session) => {
    isEditing.value = true
    editingId.value = session.id
    form.clearErrors()

    form.id        = session.id
    form.name      = session.name
    form.starts_at = formatForDateInput(session.starts_at)
    form.ends_at   = formatForDateInput(session.ends_at)

    const firstTerm  = session.terms?.find(t => t.name === 'First Term')
    const secondTerm = session.terms?.find(t => t.name === 'Second Term')
    const thirdTerm  = session.terms?.find(t => t.name === 'Third Term')

    form.first_term_starts_at  = formatForDateInput(firstTerm?.starts_at)
    form.first_term_ends_at    = formatForDateInput(firstTerm?.ends_at)
    form.second_term_starts_at = formatForDateInput(secondTerm?.starts_at)
    form.second_term_ends_at   = formatForDateInput(secondTerm?.ends_at)
    form.third_term_starts_at  = formatForDateInput(thirdTerm?.starts_at)
    form.third_term_ends_at    = formatForDateInput(thirdTerm?.ends_at)

    // store session for isSessionClosed check
    currentSession.value = session
    show.value = true
}

defineExpose({ open })

/* -------------------- FORM -------------------- */
const currentSession = ref(null)

const isSessionClosed = computed(() =>
    currentSession.value ? !currentSession.value.is_active : false
)

const form = useForm({
    id:                    null,
    name:                  null,
    starts_at:             null,
    ends_at:               null,
    first_term_starts_at:  null,
    first_term_ends_at:    null,
    second_term_starts_at: null,
    second_term_ends_at:   null,
    third_term_starts_at:  null,
    third_term_ends_at:    null,
})

const formatForDateInput = (datetime) => datetime ? datetime.split('T')[0] : null

const handleClose = () => {
    form.reset()
    form.clearErrors()
    isEditing.value      = false
    editingId.value      = null
    currentSession.value = null
    show.value           = false
}

const handleSubmit = () => {
    form.put(`/staff/academic-sessions/${form.id}`, {
        onSuccess: (page) => { handleClose(); flashToast(page.props.flash) },
        onError:   ()     => flashToast({ type: 'error', message: 'Check form for errors' }),
    })
}

/* Clamp term dates within session range */
watch(
    () => [form.starts_at, form.ends_at],
    ([start, end]) => {
        if (!start || !end) return
        const clamp = (date) => {
            if (!date) return null
            if (date < start) return start
            if (date > end)   return end
            return date
        }
        form.first_term_starts_at  = clamp(form.first_term_starts_at)
        form.first_term_ends_at    = clamp(form.first_term_ends_at)
        form.second_term_starts_at = clamp(form.second_term_starts_at)
        form.second_term_ends_at   = clamp(form.second_term_ends_at)
        form.third_term_starts_at  = clamp(form.third_term_starts_at)
        form.third_term_ends_at    = clamp(form.third_term_ends_at)
    }
)
</script>

<template>
    <Modal
        v-model="show"
        title="Edit Session"
        size="medium"
        @close="handleClose"
    >
        <form @submit.prevent="handleSubmit" class="p-6 space-y-5">

            <Input
                v-model="form.name"
                label="Session Name"
                type="text"
                placeholder="e.g. 2025/2026"
                :error="form.errors.name"
            />

            <div class="grid grid-cols-2 gap-4">
                <Input
                    v-model="form.starts_at"
                    label="Start Date"
                    type="date"
                    :error="form.errors.starts_at"
                />
                <Input
                    v-model="form.ends_at"
                    label="End Date"
                    type="date"
                    :error="form.errors.ends_at"
                />
            </div>

            <!-- Term dates -->
            <div class="pt-2 border-t border-gray-100">
                <p class="text-sm font-semibold text-gray-700 mb-4">Term Dates</p>

                <div
                    v-for="term in [
                        { label: 'First Term',  start: 'first_term_starts_at',  end: 'first_term_ends_at'  },
                        { label: 'Second Term', start: 'second_term_starts_at', end: 'second_term_ends_at' },
                        { label: 'Third Term',  start: 'third_term_starts_at',  end: 'third_term_ends_at'  },
                    ]"
                    :key="term.label"
                    class="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100"
                >
                    <p class="text-xs font-medium text-gray-500 mb-3">{{ term.label }}</p>
                    <div class="grid grid-cols-2 gap-3">
                        <Input
                            v-model="form[term.start]"
                            label="Start Date"
                            type="date"
                            :error="form.errors[term.start]"
                            :disabled="isSessionClosed"
                        />
                        <Input
                            v-model="form[term.end]"
                            label="End Date"
                            type="date"
                            :error="form.errors[term.end]"
                            :disabled="isSessionClosed"
                        />
                    </div>
                </div>
            </div>

            <button
                type="submit"
                :disabled="form.processing || isSessionClosed"
                class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold transition
                       flex justify-center items-center gap-2
                       bg-edit hover:bg-edit-light
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <Spinner v-if="form.processing" :size="14" />
                Update Session
            </button>

        </form>
    </Modal>
</template>